import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import clsx from "clsx";
import { Loader } from "react-bootstrap-typeahead";
import {
  useUpdateSMTPMailConfigMutation,
  useSmtpMailConfigMutation,
} from "../../../../Services/settingsAPIs/EmailConfiguration";

interface IPROPS {
  closeModal: () => void;
  refetch: any;
  mailConfigForEdit: any;
}

const registrationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Wrong email format")
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Minimum 8 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required"),
  hostname: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Hostname is required"),
  port: Yup.number()
    .required("Port is required")
    .typeError("Port must be a number"),
});

const AddEmailConfigModal: React.FC<IPROPS> = ({
  closeModal,
  refetch,
  mailConfigForEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [mailServer, setMailServer] = useState<string>("gmail");
  const [showPassword, setShowPassword] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const [trigger, { isLoading: emailConfigLoading }] =
    useSmtpMailConfigMutation();
  const [triggerEmailConfigEditMutation] = useUpdateSMTPMailConfigMutation();

  const initialValues = {
    email: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.username
      : "",
    password: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.password
      : "",
    hostname: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.host_name
      : "",
    port: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.port
      : "",
    client_id: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.client_id
      : "",
    client_secret: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.client_secret
      : "",
    server_tenant_id: mailConfigForEdit.mailConfigEditEnabled
      ? mailConfigForEdit.mailConfigData.server_tenant_id
      : "",
  };

  const resetForm = () => {
    formik.setFieldValue("hostname", "");
    formik.setFieldValue("port", "");
    formik.setErrors({});
    formik.setTouched({});
  };

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);

      const emailConfigPayload = {
        client_id: values?.client_id,
        client_secret: values?.client_secret,
        email_server: mailServer,
        host_name: values.hostname,
        password: values?.password,
        port: values?.port,
        server_tenant_id: values?.server_tenant_id,
        disposition: null,
        workflow: null,
        team: null,
        user: [],
        username: values.email,
      };

      try {
        if (mailConfigForEdit.mailConfigEditEnabled) {
          const res: any = await triggerEmailConfigEditMutation({
            configId: mailConfigForEdit.mailConfigData.id,
            body: emailConfigPayload,
          });
          if (res.data) {
            toast.success("Updated the SMTP mail configuration successfully");
            closeModal();
            refetch();
          } else {
            toast.error("Failed to update");
          }
        } else {
          const res: any = await trigger(emailConfigPayload);
          if (res.data) {
            toast.success(
              "A new SMTP based email configuration has been created successfully"
            );
            refetch();
            closeModal();
          } else if (res.error) {
            if ("status" in res.error && res.error.status === 400) {
              if (
                typeof res.error.data === "object" &&
                res.error.data !== null
              ) {
                const errorData = res.error.data as { username?: string[] };
                if (errorData.username && errorData.username.length > 0) {
                  toast.error(errorData.username[0]);
                } else {
                  toast.error("An error occurred");
                }
              }
            } else {
              console.error("Unhandled error:");
            }
          }
        }
      } catch (error) {
        console.error("Error making the POST request:", error);
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (mailConfigForEdit.mailConfigEditEnabled) {
      const hostName = mailConfigForEdit?.mailConfigData?.email_server;
      setMailServer(hostName === "microsoft" ? "microsoft" : "gmail");
    }
  }, [mailConfigForEdit]);

  // ---------- Handle click outside ----------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeModal]);
  // ----------------------------------------

  return (
    <>
      <div ref={modalRef}>
        <form
          onSubmit={formik.handleSubmit}
          className="form w-100"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Close Button */}
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "end",
            }}
          >
            <i
              style={{
                fontSize: "26px",
                color: "black",
                cursor: "pointer",
              }}
              className="bi bi-x"
              onClick={closeModal}
            ></i>
          </div>

          {/* Title */}
          <div className="text-center mb-4">
            <label className="form-label text-dark card-title">
              SMTP <br />
              Email configuration
            </label>
          </div>

          <div className="w-100">
            {/* Mail Server Selection */}
            <div className="mt-2">
              <label htmlFor="email_server">Email server</label>
              <select
                className="form-control form-control-lg form-control-solid"
                id="email_server"
                value={mailServer}
                onChange={(e) => {
                  setMailServer(e.target.value);
                  resetForm();
                }}
              >
                <option value="gmail">Gmail</option>
                <option value="microsoft">Microsoft</option>
              </select>
            </div>

            {/* Email Field - Common for both */}
            <div className="mt-2">
              <label className="required">Email</label>
              <input
                type="text"
                {...formik.getFieldProps("email")}
                className={clsx(
                  "form-control form-control-lg form-control-solid",
                  {
                    "is-invalid": formik.touched.email && formik.errors.email,
                  },
                  {
                    "is-valid": formik.touched.email && !formik.errors.email,
                  }
                )}
                placeholder="Enter email"
              />
              {formik.touched.email && formik.errors.email && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <span role="alert" style={{ color: "red" }}>
                      {formik.errors.email}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Password Field - Common for both */}
            <div className="mt-2">
              <label className="required">Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  {...formik.getFieldProps("password")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.password && formik.errors.password,
                    },
                    {
                      "is-valid":
                        formik.touched.password && !formik.errors.password,
                    }
                  )}
                  placeholder="Enter password"
                  style={{ paddingRight: "40px" }}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "40px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#666",
                  }}
                >
                  {showPassword ? (
                    <i className="bi bi-eye-slash"></i>
                  ) : (
                    <i className="bi bi-eye"></i>
                  )}
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <span role="alert" style={{ color: "red" }}>
                      {formik.errors.password}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Microsoft-specific Fields */}
            {mailServer === "microsoft" && (
              <>
                <div className="mt-2">
                  <label className="required">Client ID</label>
                  <input
                    type="text"
                    {...formik.getFieldProps("client_id")}
                    className="form-control form-control-lg form-control-solid"
                    placeholder="Enter client ID"
                  />
                </div>
                <div className="mt-2">
                  <label className="required">Client Secret</label>
                  <input
                    type="text"
                    {...formik.getFieldProps("client_secret")}
                    className="form-control form-control-lg form-control-solid"
                    placeholder="Enter client secret"
                  />
                </div>
                <div className="mt-2">
                  <label className="required">Server Tenant ID</label>
                  <input
                    type="text"
                    {...formik.getFieldProps("server_tenant_id")}
                    className="form-control form-control-lg form-control-solid"
                    placeholder="Enter tenant ID"
                  />
                </div>
              </>
            )}

            {/* Common Fields */}
            <div className="mt-2">
              <label className="required">SMTP server address</label>
              <input
                type="text"
                {...formik.getFieldProps("hostname")}
                className={clsx(
                  "form-control form-control-lg form-control-solid",
                  {
                    "is-invalid":
                      formik.touched.hostname && formik.errors.hostname,
                  },
                  {
                    "is-valid":
                      formik.touched.hostname && !formik.errors.hostname,
                  }
                )}
                placeholder="smtp.example.com"
              />
              {formik.touched.hostname && formik.errors.hostname && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <span role="alert" style={{ color: "red" }}>
                      {formik.errors.hostname}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2">
              <label className="required">Port</label>
              <input
                type="text"
                {...formik.getFieldProps("port")}
                className={clsx(
                  "form-control form-control-lg form-control-solid",
                  {
                    "is-invalid": formik.touched.port && formik.errors.port,
                  },
                  {
                    "is-valid": formik.touched.port && !formik.errors.port,
                  }
                )}
                placeholder="465"
              />
              {formik.touched.port && formik.errors.port && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <span role="alert" style={{ color: "red" }}>
                      {formik.errors.port}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div
              className="mt-2"
              style={{
                display: "flex",
                gap: "10px",
                width: "100%",
                justifyContent: "end",
              }}
            >
              <button
                type="button"
                className="btn btn-light"
                onClick={closeModal}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={emailConfigLoading}
              >
                {emailConfigLoading ? (
                  <>
                    Submitting... <Loader />
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddEmailConfigModal;
